import { CUSTOMER_REVIEWS } from "@shared/constants/event-detail";
import Divider from "@shared/components/ui-kit/data-display/Divider/Divider.web";
import FiveStarRating from "@shared/components/ui-kit/data-display/FiveStarRating/FiveStarRating.web";
import Skeleton from "@shared/components/ui-kit/feedback/Skeleton/Skeleton.web";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import { PublicRatingSerializer } from "@shared/modules/events/types/public";
import { formatDate } from "@shared/utils/dates/formatting";
import { useTranslation } from "@shared/utils/internationalization";
import Text from "@ui-kit/typography/Text/Text.web";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import { PublicEventReviewSerializer } from "../../data/types/events";

const getReviewDateFormat = (date: string) => {
  return formatDate({
    date,
    format: "D MMM YYYY",
    timezone: null,
  });
};

export interface CustomerReviewsProps {
  overallRating: PublicRatingSerializer;
  /** List of reviews to display */
  reviews: PublicEventReviewSerializer[];
  isLoading: boolean;
  hasNoResults: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  /** Currently active star filter (e.g., "5" for 5-star reviews) */
  activeStarRating: string;
  /** Callback when user clicks a star rating filter */
  onStarRatingChange: (rating: string) => void;
  /** Whether to show the rating distribution breakdown */
  showDistribution: boolean;
}

/**
 * Presentational component for displaying customer reviews.
 * All data fetching and source logic should be handled externally.
 * Use `useCustomerReviewsData` hook to get the data for this component.
 */
export default function CustomerReviews({
  overallRating,
  reviews,
  isLoading,
  hasNoResults,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  activeStarRating,
  onStarRatingChange,
  showDistribution,
}: CustomerReviewsProps) {
  return (
    <Flex
      gap={{ base: "8", sm: "12" }}
      direction={{ base: "column", sm: "row" }}
      alignItems={{ base: "center", sm: "flex-start" }}
      mt="xs"
    >
      <CustomerReviews.OverallRating
        overallRating={overallRating}
        setActiveStarRating={onStarRatingChange}
        activeStarRating={activeStarRating}
        showDistribution={showDistribution}
      />
      <CustomerReviews.ReviewList
        reviews={reviews}
        isLoading={isLoading}
        hasNoResults={hasNoResults}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={onLoadMore}
      />
    </Flex>
  );
}

CustomerReviews.StarRatingCount = function StarRatingCount({
  rating,
  count,
  isActive,
  onPress,
}: {
  rating: number;
  count: number;
  isActive: boolean;
  onPress: () => void;
}) {
  const { t } = useTranslation("common");

  return (
    <Flex
      direction="row"
      width="full"
      alignItems="center"
      gap="2xs"
      onClick={onPress}
      bg={isActive ? "gray.200" : "transparent"}
      borderRadius="full"
      py="0.5"
      pl="2.5"
      cursor="pointer"
    >
      <Text
        color="gray.500"
        fontSize={{ base: "0.75rem", sm: "0.875rem" }}
        textAlign="right"
        whiteSpace="nowrap"
        flexShrink={0}
      >
        {t("{{rating}} stars", { rating })}
      </Text>
      <FiveStarRating rating={rating} />
      <Text
        flex={1}
        color="gray.500"
        fontSize={{ base: "0.75rem", sm: "0.875rem" }}
        textAlign="left"
      >
        {`(${count})`}
      </Text>
    </Flex>
  );
};

CustomerReviews.OverallRating = function OverallRating({
  overallRating,
  activeStarRating,
  setActiveStarRating,
  showDistribution,
}: {
  overallRating: PublicRatingSerializer;
  activeStarRating: string;
  setActiveStarRating: (value: string) => void;
  showDistribution: boolean;
}) {
  const { t } = useTranslation("common");
  const { total_ratings, average_rating, distribution } = overallRating;
  const numericAverageRating = parseFloat(String(average_rating));
  const roundedAverageRating = Math.round(numericAverageRating);

  return (
    <Flex direction="column" minWidth="element.12xl">
      <Flex
        direction="row"
        borderRadius="full"
        bg="primary.800"
        px="sm"
        py="2xs"
        width="full"
        justifyContent="space-between"
        alignItems="center"
      >
        <FiveStarRating rating={roundedAverageRating} />
        <Text
          fontSize={{ base: "0.75rem", sm: "0.875rem" }}
          color="fg.inverted"
        >
          {t("{{rating}} out of 5", {
            rating: numericAverageRating.toFixed(1),
          })}
        </Text>
      </Flex>

      <Text
        textAlign="center"
        color="gray.500"
        fontSize={{ base: "0.75rem", sm: "0.875rem" }}
        mt="2xs"
        mb="xs"
      >
        {t("{{count}} customer reviews", { count: total_ratings })}
      </Text>

      {showDistribution && (
        <Flex direction="column" gap="2xs" alignItems="center">
          {Object.keys(distribution)
            .sort((a, b) => Number(b) - Number(a))
            .map((rating) => (
              <CustomerReviews.StarRatingCount
                key={rating}
                rating={Number(rating)}
                count={distribution[rating]}
                isActive={activeStarRating === rating}
                onPress={() => setActiveStarRating(rating)}
              />
            ))}
        </Flex>
      )}
    </Flex>
  );
};

CustomerReviews.ExpandableReviewText = function ExpandableReviewText({
  text,
  maxLines = 5,
}: {
  text: string;
  maxLines?: number;
}) {
  const { t } = useTranslation("common");

  return (
    <Text.Expandable
      maxLines={maxLines}
      showMoreLabel={t("Show more")}
      showLessLabel={t("Show less")}
      fontSize={{ base: "0.75rem", sm: "0.875rem" }}
      lineHeight="tall"
    >
      {text}
    </Text.Expandable>
  );
};

CustomerReviews.OrganizerResponse = function OrganizerResponse({
  organizerResponse,
}: {
  organizerResponse: PublicEventReviewSerializer["organizer_response_data"];
}) {
  const { t } = useTranslation("common");

  if (!organizerResponse) {
    return null;
  }

  const { response, responded_at } = organizerResponse;

  return (
    <Flex
      direction="column"
      alignItems="flex-start"
      gap="2xs"
      mt="xs"
      borderLeft="4px solid"
      borderLeftColor="primary.500"
      p="xs"
      bg="gray.100"
      borderRadius="md"
    >
      <Box>
        <Text
          fontWeight="semibold"
          fontSize={{ base: "0.75rem", sm: "0.875rem" }}
        >
          {t(CUSTOMER_REVIEWS.RESPONSE_FROM_ORGANIZER)}
        </Text>
        <Text
          fontSize={{ base: "0.75rem", sm: "0.875rem" }}
          color="gray.600"
          mt="2xs"
        >
          {getReviewDateFormat(responded_at)}
        </Text>
      </Box>
      <CustomerReviews.ExpandableReviewText text={response} />
    </Flex>
  );
};

CustomerReviews.ReviewHeading = function ReviewHeading({
  review,
}: {
  review: PublicEventReviewSerializer;
}) {
  const { t } = useTranslation("common");
  return (
    <Flex direction="column" width="full" gap="0.5">
      <Flex justifyContent="space-between">
        <Flex alignItems="center" gap="2xs">
          <Text
            fontWeight="semibold"
            fontSize={{ base: "0.75rem", sm: "0.875rem" }}
          >
            {review.author_name}
          </Text>
          {review.verified && (
            <Box
              display="inline-flex"
              alignItems="center"
              gap="0.5"
              px="2xs"
              py="0.5"
              borderRadius="sm"
              border="1px solid"
              borderColor="primary.800"
              backgroundColor="primary.100"
            >
              <Icon.Checkmark
                boxSize={{ base: "0.625rem", sm: "0.75rem" }}
                color="primary.800"
              />
              <Text
                fontSize={{ base: "0.5rem", sm: "0.625rem" }}
                color="primary.800"
                fontWeight="medium"
                lineHeight="1"
              >
                {t("Verified buyer")}
              </Text>
            </Box>
          )}
        </Flex>
        <FiveStarRating rating={review.rating_value} />
      </Flex>
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontSize={{ base: "0.75rem", sm: "0.875rem" }}>
          {review.event_reviewed_name || ""}
        </Text>
        <Text fontSize={{ base: "0.75rem", sm: "0.875rem" }} color="gray.600">
          {getReviewDateFormat(review.created)}
        </Text>
      </Flex>
    </Flex>
  );
};

CustomerReviews.ReviewList = function ReviewList({
  reviews,
  isLoading,
  hasNoResults,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  reviews: PublicEventReviewSerializer[];
  isLoading?: boolean;
  hasNoResults?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore: () => void;
}) {
  const { t } = useTranslation("common");

  if (hasNoResults) {
    return (
      <Flex width="full" justifyContent="center">
        <Text fontSize={{ base: "0.75rem", sm: "0.875rem" }}>
          {t("No reviews found")}
        </Text>
      </Flex>
    );
  }

  if (isLoading) {
    return <CustomerReviews.ReviewListSkeleton />;
  }

  return (
    <Flex
      direction="column"
      gap="md"
      width="full"
      pb="2xs"
      pr={{ base: "none", sm: "2xs" }}
    >
      <Divider display={{ base: "block", sm: "none" }} />
      {reviews.map((review, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index}>
          <VStack alignItems="flex-start" spacing="3">
            <CustomerReviews.ReviewHeading review={review} />
            {review.rating_explanation && (
              <CustomerReviews.ExpandableReviewText
                text={review.rating_explanation}
              />
            )}
          </VStack>
          <CustomerReviews.OrganizerResponse
            organizerResponse={review.organizer_response_data}
          />
          {index !== reviews.length - 1 && <Divider mt="md" />}
        </Box>
      ))}

      {isFetchingNextPage && <CustomerReviews.ReviewListSkeleton />}

      {hasNextPage && !isLoading && !isFetchingNextPage && (
        <Flex justifyContent="center" mt="2xs">
          <Button.Ghost
            onClick={onLoadMore}
            data-testid="review-list-load-more-button"
            isLoading={isFetchingNextPage}
            fontSize={{ base: "0.75rem", sm: "0.875rem" }}
          >
            {t("Load more")}
          </Button.Ghost>
        </Flex>
      )}
    </Flex>
  );
};

CustomerReviews.ReviewListSkeleton = function ReviewListSkeleton() {
  return (
    <Flex direction="column" gap="2xs" width="full">
      <Divider display={{ base: "block", sm: "none" }} />
      {[...Array(3)].map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index}>
          <VStack alignItems="flex-start" spacing="3">
            <Flex direction="row" justifyContent="space-between" width="full">
              <Box>
                <Skeleton
                  height="element.lg"
                  width="element.8xl"
                  borderRadius="md"
                  mb="2xs"
                />
                <Skeleton
                  height="element.md"
                  width="element.7xl"
                  borderRadius="md"
                />
              </Box>
              <Flex direction="row" gap="2xs">
                {[...Array(5)].map((_, starIndex) => (
                  <Skeleton
                    // eslint-disable-next-line react/no-array-index-key
                    key={starIndex}
                    width="element.md"
                    height="element.md"
                    borderRadius="sm"
                  />
                ))}
              </Flex>
            </Flex>
            <Box width="full">
              <Skeleton
                height="element.md"
                width="100%"
                borderRadius="md"
                mb="2xs"
              />
              <Skeleton
                height="element.md"
                width="95%"
                borderRadius="md"
                mb="2xs"
              />
              <Skeleton height="element.md" width="80%" borderRadius="md" />
            </Box>
          </VStack>
          {index !== 2 && <Divider mt="2xs" />}
        </Box>
      ))}
    </Flex>
  );
};
